import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  Organization,
  CreateOrganizationInput,
  UpdateOrganizationInput,
} from "../types";

const ORGANIZATION_FIELDS = `
  id
  name
  logo
  logoKey
  ownerEmail
  status
  planId
  brokerId
  timezone
  dateformat
  timeFormat
  tableLayoutLimit
  tableLayoutUsage
  createdAt
  updatedAt
`;

export const organizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<Organization[], void>({
      query: () => ({
        body: {
          query: `
            query Organizations {
              organizations {
                ${ORGANIZATION_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Organization[]>(res, "organizations"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((o) => ({
                type: "Organization" as const,
                id: o.id,
              })),
              { type: "Organization" as const, id: "LIST" },
            ]
          : [{ type: "Organization" as const, id: "LIST" }],
    }),

    getOrganization: builder.query<Organization, string>({
      query: (id) => ({
        body: {
          query: `
            query Organization($id: String!) {
              organization(id: $id) {
                ${ORGANIZATION_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Organization>(res, "organization"),

      providesTags: (_result, _err, id) => [{ type: "Organization", id }],
    }),

    // `createOrganization` / `updateOrganization` accept a `JSON!` payload and
    // return `JSON` on the services side, so results are loosely typed.
    createOrganization: builder.mutation<
      Record<string, unknown>,
      CreateOrganizationInput
    >({
      query: (input) => ({
        body: {
          query: `
            mutation CreateOrganization($input: JSON!) {
              createOrganization(input: $input)
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Record<string, unknown>>(res, "createOrganization"),

      invalidatesTags: [{ type: "Organization", id: "LIST" }],
    }),

    updateOrganization: builder.mutation<
      Record<string, unknown>,
      { id: string; input: UpdateOrganizationInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateOrganization($id: String!, $input: JSON!) {
              updateOrganization(id: $id, input: $input)
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Record<string, unknown>>(res, "updateOrganization"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Organization", id },
        { type: "Organization", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
} = organizationApi;
